import { AlphaHttpClient, calculateZindex, LanguagesService, SessionTimeoutService } from '@alpha-core/platform-ui';
import { Component, HostListener, inject, OnInit, Renderer2 } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { NgHttpLoaderModule, Spinkit, SpinnerVisibilityService } from 'ng-http-loader';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { Subscription } from 'rxjs';
import { LoaderComponent } from './base-layout/components/loader/loader.component';
import { TranslatePipe } from './pipes/translate.pipe';
import { userService } from './services/user-service';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [RouterOutlet, NgHttpLoaderModule, ConfirmDialogModule, ToastModule, TranslatePipe],
    providers: [ConfirmationService],
    templateUrl: './app.component.html',
    styleUrl: './app.component.css',
})
export class AppComponent implements OnInit {
    private spinner = inject(SpinnerVisibilityService);
    private languageService = inject(LanguagesService);
    private renderer = inject(Renderer2);

    title = 'alpha-admin-ui';
    public spinkit = Spinkit;
    activeRequests: Set<string> | null = new Set();
    loaderComponent = LoaderComponent;
    dir = 'ltr';
    selectedLanguageSubscription: Subscription | null = null;

    statusIcons: any = {
        success: { src: 'assets/icons/status-success.svg' },
        warn: { src: 'assets/icons/status-warn.svg' },
        error: { src: 'assets/icons/status-error.svg' },
        info: { src: 'assets/icons/status-info.svg' },
    };

    private messageService = inject(MessageService);
    private userService = inject(userService);
    private sessionTimeoutService = inject(SessionTimeoutService);
    private httpClient = inject(AlphaHttpClient);

    async ngOnInit() {
        window.addEventListener('http-plugin:httprequest', (data) => {
            const customEvent = data as CustomEvent;
            const reqId = customEvent?.detail?.reqId;
            const hideSpinner = customEvent?.detail?.hideSpinner;
            if (this.activeRequests && reqId && !hideSpinner) {
                this.activeRequests.add(reqId);
                if (this.activeRequests.size === 1) {
                    this.spinner.show();
                }
            }
        });
        window.addEventListener('http-plugin:httpresponse', (data) => {
            const reqId = (data as CustomEvent)?.detail?.reqId;
            if (this.activeRequests && reqId) {
                this.activeRequests.delete(reqId);
                if (!this.activeRequests.size) {
                    this.spinner.hide();
                }
            }
        });

        window.addEventListener('http-plugin:httpreset', (data) => {
            this.activeRequests = null;
            this.spinner.hide();
        });

        this.selectedLanguageSubscription = this.languageService.selectedLanguage$.subscribe((language) => {
            if (language) this.dir = language.dir;
            this.renderer.setAttribute(document.body, 'dir', this.dir);
        });

        const listener = () => {
            this.userService.updateUserToLocal();
            window.removeEventListener('alpha_ready', listener);
        };
        window.addEventListener('alpha_ready', listener);
    }

    @HostListener('window:alpha-renderer:toast-message', ['$event'])
    onAlphaRendererToastMessage(event: CustomEvent) {
        this.showToast(event);
    }

    showToast(event: any) {
        event.detail.toasts.forEach((result: any) => {
            if (result?.toastType)
                this.messageService.add({
                    ...result.message,
                    severity: result.toastType,
                    life: result?.duration || 2000,
                });
        });
    }

    getZindex() {
        return calculateZindex();
    }

    ngOnDestroy() {
        if (this.selectedLanguageSubscription) {
            this.selectedLanguageSubscription.unsubscribe();
        }
        if (window.environments.ENABLE_IDLE_SESSION_TIMEOUT) {
            this.sessionTimeoutService.stopMonitoring();
        }
    }
}
